import { useState } from "react";
import { Check, Loader2, Lock, Pencil, Trash2, X, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { HubspotPartnerFieldMapRow } from "@colab/shared";
import {
  useDeletePartnerFieldMap,
  useUpdatePartnerFieldMap,
} from "../use-partner-field-map-mutations";

// The 8 core fields are env-mapped, not stored in hubspot_partner_field_map.
// We synthesise read-only rows for them so the table shows the full
// mapping picture in one place.
const CORE_ROWS: Array<{
  property: string;
  type: HubspotPartnerFieldMapRow["hubspot_type"];
  label: string;
}> = [
  { property: "company_name", type: "string", label: "Company name" },
  { property: "partner_type", type: "enumeration", label: "Partner type" },
  { property: "tier", type: "enumeration", label: "Tier" },
  { property: "status", type: "enumeration", label: "Status" },
  { property: "primary_contact_name", type: "string", label: "Primary contact name" },
  { property: "primary_contact_email", type: "string", label: "Primary contact email" },
  { property: "website", type: "string", label: "Website" },
  { property: "region", type: "string", label: "Region" },
];

export function PartnerFieldMappingTable({
  rows,
}: {
  rows: HubspotPartnerFieldMapRow[];
}) {
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left">HubSpot property</th>
            <th className="px-3 py-2 text-left">Type</th>
            <th className="px-3 py-2 text-left">Display label</th>
            <th className="px-3 py-2 text-left">Section</th>
            <th className="px-3 py-2 text-right">Enabled</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {CORE_ROWS.map((r) => (
            <tr key={`core-${r.property}`} className="bg-muted/10">
              <td className="px-3 py-2 font-mono text-xs">{r.property}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.type}</td>
              <td className="px-3 py-2 font-medium">{r.label}</td>
              <td className="px-3 py-2 text-muted-foreground">Core</td>
              <td className="px-3 py-2 text-right text-muted-foreground">
                <Lock className="ml-auto h-3 w-3" />
              </td>
              <td className="px-3 py-2 text-right text-muted-foreground">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-[10px] uppercase">env</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Mapped via HUBSPOT_PARTNER_PROPERTY_MAP env var.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr className="bg-background">
              <td colSpan={6} className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                Extras
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <ExtrasRow key={row.id} row={row} existingSections={Array.from(new Set(rows.map((r) => r.display_section)))} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExtrasRow({
  row,
  existingSections,
}: {
  row: HubspotPartnerFieldMapRow;
  existingSections: string[];
}) {
  const update = useUpdatePartnerFieldMap();
  const del = useDeletePartnerFieldMap();
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(row.display_label);
  const [draftSection, setDraftSection] = useState(row.display_section);

  function save() {
    update.mutate(
      {
        id: row.id,
        input: {
          display_label: draftLabel.trim(),
          display_section: draftSection.trim(),
        },
      },
      { onSuccess: () => setEditing(false) },
    );
  }
  function cancel() {
    setDraftLabel(row.display_label);
    setDraftSection(row.display_section);
    setEditing(false);
  }
  function toggleEnabled(next: boolean) {
    update.mutate({ id: row.id, input: { enabled: next } });
  }

  const sectionListId = `partner-field-section-list`;
  const pending = update.isPending;

  return (
    <tr className={row.enabled ? "" : "opacity-50"}>
      <td className="px-3 py-2 font-mono text-xs">
        <span className="inline-flex items-center gap-1.5">
          {row.is_stale && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                </TooltipTrigger>
                <TooltipContent>
                  Property no longer exists in HubSpot.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {row.hubspot_property_name}
        </span>
      </td>
      <td className="px-3 py-2 text-muted-foreground">{row.hubspot_type}</td>
      <td className="px-3 py-2">
        {editing ? (
          <Input
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            className="h-7 text-xs"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") cancel();
            }}
          />
        ) : (
          <span className="font-medium">{row.display_label}</span>
        )}
      </td>
      <td className="px-3 py-2">
        {editing ? (
          <>
            <Input
              value={draftSection}
              onChange={(e) => setDraftSection(e.target.value)}
              className="h-7 text-xs"
              list={sectionListId}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") cancel();
              }}
            />
            <datalist id={sectionListId}>
              {existingSections.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </>
        ) : (
          <span className="text-muted-foreground">{row.display_section}</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <Switch
          checked={row.enabled}
          onCheckedChange={toggleEnabled}
          disabled={pending && !editing}
        />
      </td>
      <td className="px-3 py-2 text-right">
        {editing ? (
          <div className="inline-flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={cancel} disabled={pending} className="h-7 px-2" title="Cancel">
              <X className="h-3 w-3" />
            </Button>
            <Button size="sm" onClick={save} disabled={pending || !draftLabel.trim() || !draftSection.trim()} className="h-7 px-2" title="Save">
              {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            </Button>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="h-7 px-2" title="Edit display label / section">
              <Pencil className="h-3 w-3" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" title="Remove">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove {row.display_label}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Stored values for <code>{row.hubspot_property_name}</code>{" "}
                    will be deleted from every partner. HubSpot is unaffected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => del.mutate(row.id)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </td>
    </tr>
  );
}
